/**
 * Texture uploads and the GPU texture cache (W2-01a preview-gl; ARCHITECTURE.md §7.3, rendering.md R§3.5, R§4.7).
 *
 * Phira's gameplay textures are plain bilinear without mipmaps, clamped, uploaded straight (not premultiplied):
 * `UNPACK_PREMULTIPLY_ALPHA_WEBGL = false`, no y flip (row 0 = the image's top row, so texture v = image v), no
 * colour-space conversion. Hold bodies of `holdRepeat` skins wrap with REPEAT (WebGL2 allows REPEAT on any size
 * without mipmaps). ImageBitmaps carry their own alpha mode: every provider creates them with
 * `premultiplyAlpha: 'none'`.
 *
 * GPU textures are derived data: the cache maps CPU-side sources (ImageBitmaps, canvases) to textures, uploads on
 * first use, evicts entries unused for a while, and is dropped whole on context loss (everything is re-uploaded from
 * the CPU sources afterwards).
 */

import { glObject } from './glState';

export type TexWrap = 'clamp' | 'repeat';

/** A source the cache can upload (objects only: they are the cache keys). */
export type TexSource =
  ImageBitmap | HTMLCanvasElement | OffscreenCanvas | HTMLImageElement | ImageData | HTMLVideoElement;

/** Pixel size of a source (0 x 0 when unknown). */
export function sourceSize(src: TexSource): readonly [number, number] {
  if (typeof HTMLVideoElement !== 'undefined' && src instanceof HTMLVideoElement)
    return [src.videoWidth, src.videoHeight];
  return [src.width, src.height];
}

/** Unpack state for straight, unflipped uploads (set once per context; WebGL keeps it as context state). */
export function setUnpackDefaults(gl: WebGL2RenderingContext): void {
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
}

/** Sampler parameters: LINEAR min/mag, no mipmaps, CLAMP_TO_EDGE or REPEAT. */
export function setSampling(gl: WebGL2RenderingContext, wrap: TexWrap): void {
  const w = wrap === 'repeat' ? gl.REPEAT : gl.CLAMP_TO_EDGE;
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, w);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, w);
}

/** Uploads `src` into `tex` (bound to TEXTURE0 afterwards). */
export function uploadInto(gl: WebGL2RenderingContext, tex: WebGLTexture, src: TexSource, wrap: TexWrap): void {
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, tex);
  setSampling(gl, wrap);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src);
}

/** A new texture holding `src`. */
export function createTextureFrom(gl: WebGL2RenderingContext, src: TexSource, wrap: TexWrap): WebGLTexture | null {
  const tex = glObject(gl.createTexture());
  if (tex === null) return null;
  uploadInto(gl, tex, src, wrap);
  return tex;
}

/** A 1 x 1 texture of one RGBA colour (bytes). */
export function createSolidTexture(
  gl: WebGL2RenderingContext,
  r: number,
  g: number,
  b: number,
  a: number,
): WebGLTexture | null {
  const tex = glObject(gl.createTexture());
  if (tex === null) return null;
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, tex);
  setSampling(gl, 'clamp');
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([r, g, b, a]));
  return tex;
}

interface Entry {
  readonly tex: WebGLTexture;
  readonly bytes: number;
  lastUsed: number;
  /** Pinned entries (skin textures) are never evicted by age. */
  pinned: boolean;
}

/** Frames an unpinned texture may stay unused before it is deleted. */
export const TEXTURE_IDLE_FRAMES = 600;
/** Eviction runs every this many frames. */
const SWEEP_EVERY = 120;

/** Source -> GPU texture cache (one per context). */
export class TextureCache {
  private readonly gl: WebGL2RenderingContext;
  private readonly bySource = new WeakMap<object, Map<TexWrap, Entry>>();
  /** Every live entry with its key (WeakMap is not iterable). */
  private readonly live = new Map<Entry, { readonly src: object; readonly wrap: TexWrap }>();
  private frame = 0;
  private bytesTotal = 0;
  readonly white: WebGLTexture | null;

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
    this.white = createSolidTexture(gl, 255, 255, 255, 255);
  }

  /** Start of a frame: advances the age clock and evicts idle textures now and then. */
  beginFrame(): void {
    this.frame++;
    if (this.frame % SWEEP_EVERY !== 0) return;
    for (const [e, key] of this.live) {
      if (e.pinned || this.frame - e.lastUsed < TEXTURE_IDLE_FRAMES) continue;
      this.remove(e, key.src, key.wrap);
    }
  }

  /** GPU texture of `src` (uploaded on first use). Null when the context cannot create one. */
  get(src: TexSource, wrap: TexWrap = 'clamp', pinned = false): WebGLTexture | null {
    let m = this.bySource.get(src);
    let e = m?.get(wrap);
    if (e === undefined) {
      const [w, h] = sourceSize(src);
      if (!(w > 0 && h > 0)) return null;
      const tex = createTextureFrom(this.gl, src, wrap);
      if (tex === null) return null;
      e = { tex, bytes: w * h * 4, lastUsed: this.frame, pinned };
      if (m === undefined) {
        m = new Map();
        this.bySource.set(src, m);
      }
      m.set(wrap, e);
      this.live.set(e, { src, wrap });
      this.bytesTotal += e.bytes;
    }
    e.lastUsed = this.frame;
    if (pinned) e.pinned = true;
    return e.tex;
  }

  /** Re-upload a source whose pixels changed (video frames); creates the entry when missing. */
  refresh(src: TexSource, wrap: TexWrap = 'clamp'): WebGLTexture | null {
    const e = this.bySource.get(src)?.get(wrap);
    if (e === undefined) return this.get(src, wrap);
    uploadInto(this.gl, e.tex, src, wrap);
    e.lastUsed = this.frame;
    return e.tex;
  }

  /** Delete the textures of one source (all wrap modes). */
  release(src: object): void {
    const m = this.bySource.get(src);
    if (m === undefined) return;
    for (const [wrap, e] of [...m]) this.remove(e, src, wrap);
  }

  /** Delete every unpinned texture (the project media changed; text rasters re-upload on demand). */
  releaseUnpinned(): void {
    for (const [e, key] of [...this.live]) if (!e.pinned) this.remove(e, key.src, key.wrap);
  }

  /** Un-pin every entry (the skin changed; old skin textures age out or are released explicitly). */
  unpinAll(): void {
    for (const e of this.live.keys()) e.pinned = false;
  }

  private remove(e: Entry, src: object, wrap: TexWrap): void {
    this.gl.deleteTexture(e.tex);
    this.live.delete(e);
    this.bytesTotal -= e.bytes;
    const m = this.bySource.get(src);
    m?.delete(wrap);
    if (m?.size === 0) this.bySource.delete(src);
  }

  /** Approximate GPU bytes held. */
  get bytes(): number {
    return this.bytesTotal;
  }

  get count(): number {
    return this.live.size;
  }

  /** Delete everything (dispose; after a context loss the objects are already gone, deleting is harmless). */
  dispose(): void {
    for (const [e, key] of [...this.live]) this.remove(e, key.src, key.wrap);
    this.gl.deleteTexture(this.white);
  }
}
